import React, { useState, useEffect } from 'react';
import { Loader2, Heart, Trash2 } from 'lucide-react';
import { apiClient } from '../../api/client';

const RecentConversationsCard = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchConversations = async () => {
    try {
      const response = await apiClient.getRecentConversations(5);
      setConversations(response);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConversations(); }, []);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays}d ago`;
  };

  const handleFavourite = async (e, convId) => {
    e.stopPropagation();
    try {
      const result = await apiClient.toggleFavouriteConversation(convId);
      setConversations(prev =>
        prev.map(c => c.conversation_id === convId ? { ...c, is_favourite: result.is_favourite } : c)
      );
    } catch (err) {
      console.error('Failed to toggle favourite:', err);
    }
  };

  const handleDelete = async (e, conv) => {
    e.stopPropagation();
    // Deleting by ava_id wipes the WHOLE thread for that tutor and resets
    // any personalisation — confirm first.
    if (!window.confirm(`Delete all conversations with ${conv.ava_name || 'this AvA'}? This permanently removes the full history and resets the tutor.`)) return;
    try {
      // Pass the AvA id (not the session id): the backend wipes messages,
      // sessions AND resets the personalised tutor slot by ava_id — this is
      // what makes dashboard delete identical to in-chat delete.
      await apiClient.deleteConversation(conv.ava_id || conv.conversation_id);
      setConversations(prev => prev.filter(c =>
        conv.ava_id ? c.ava_id !== conv.ava_id : c.conversation_id !== conv.conversation_id
      ));
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  };

  if (loading) {
    return (
      <div className="card" data-testid="recent-conversations-card">
        <div className="card-header">
          <h2 className="card-title">Recent Conversations</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" data-testid="recent-conversations-card">
        <div className="card-header">
          <h2 className="card-title">Recent Conversations</h2>
        </div>
        <p className="text-sm text-red-500 py-4">Failed to load conversations</p>
      </div>
    );
  }

  return (
    <div className="card" data-testid="recent-conversations-card">
      <div className="card-header">
        <h2 className="card-title">Recent Conversations</h2>
        <a href="/conversations" className="card-action">See All</a>
      </div>

      {conversations.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">💬</div>
          <p className="empty-state-text">No conversations yet</p>
          <p className="text-xs text-gray-600 mt-2">Start practising with your AvA to see conversations here</p>
        </div>
      ) : (
        <div className="space-y-1 max-h-[220px] overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
          {conversations.map((conv) => (
            <div
              key={conv.conversation_id}
              className="group flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
              data-testid={`conversation-${conv.conversation_id}`}
            >
              {conv.ava_avatar ? (
                <img src={conv.ava_avatar} alt={conv.ava_name} className="w-10 h-10 rounded-full flex-shrink-0 object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full flex-shrink-0 bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-600 font-medium">{conv.ava_name?.charAt(0) || 'A'}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-semibold text-gray-900 text-sm">{conv.ava_name}</p>
                  <span className="text-xs text-gray-500">{formatTimestamp(conv.timestamp)}</span>
                </div>
                <p className="text-sm text-gray-600 truncate">{conv.snippet}</p>
                {conv.is_unread && (
                  <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">Unread</span>
                )}
              </div>
              {/* Action Icons - heart always visible, delete on hover */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={(e) => handleFavourite(e, conv.conversation_id)}
                  className="p-1.5 rounded-lg hover:bg-pink-50 transition-colors"
                  title={conv.is_favourite ? 'Remove from saved' : 'Save conversation'}
                  data-testid={`favourite-btn-${conv.conversation_id}`}
                >
                  <Heart
                    size={16}
                    className={conv.is_favourite ? 'text-pink-500' : 'text-gray-300 hover:text-pink-400'}
                    fill={conv.is_favourite ? 'currentColor' : 'none'}
                  />
                </button>
                <button
                  onClick={(e) => handleDelete(e, conv)}
                  className="p-1.5 rounded-lg hover:bg-red-50 transition-colors opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                  title="Delete conversation"
                  data-testid={`delete-btn-${conv.conversation_id}`}
                >
                  <Trash2 size={16} className="text-gray-400 hover:text-red-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecentConversationsCard;
